import React from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { X, UserCheck } from 'lucide-react';

/**
 * ImpersonationBar - Shown when a super admin is impersonating another user.
 * Displays a banner at the top of the screen with the impersonated user's info
 * and an exit button to return to the super admin account.
 */
export const ImpersonationBar: React.FC = () => {
    const { isImpersonating, impersonatedUser, exitImpersonation } = useAuth();

    if (!isImpersonating || !impersonatedUser) {
        return null;
    }

    // Show EVERY role the user holds, not just the denormalized primary one.
    // Moaaz is student+coordinator and the banner said "(student)", which made a
    // coordinator look like a plain student while debugging their permissions.
    const impersonatedRoles = (
        Array.isArray(impersonatedUser.roleNames) && impersonatedUser.roleNames.length > 0
            ? impersonatedUser.roleNames
            : [impersonatedUser.role]
    ).filter(Boolean).join(', ');

    return (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 shadow-lg">
            <div className="container max-w-6xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <UserCheck className="h-5 w-5" />
                    <span className="font-medium">
                        Viewing as: <strong>{impersonatedUser.displayName || impersonatedUser.email}</strong>
                        <span className="ms-2 text-amber-100 text-sm">
                            ({impersonatedRoles})
                        </span>
                    </span>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={exitImpersonation}
                    className="text-white hover:bg-white/20 hover:text-white gap-1"
                >
                    <X className="h-4 w-4" />
                    Exit
                </Button>
            </div>
        </div>
    );
};
